import { type QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DataflowSummary } from "@shared/types/dataflows";
import type { Overview } from "@shared/types/overview";
import type { DbtJobRunRow, TransformationGraph, DbtJobView } from "@shared/types/transformations";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { DATAFLOWS_PATH } from "@/hooks/use-dataflows";
import { instanceListFilter } from "@/lib/instanceKey";
import {
  ApiClientError,
  DBT_JOBS_GRAPH_PATH,
  DBT_JOBS_PATH,
  DBT_JOBS_RUNS_PATH,
  apiRequest,
  listDbtJobRuns,
} from "@/lib/api";
import { transformStepPresentation } from "@/components/transformations/transformFormatting";

/**
 * The Overview screen's one read (Session 10, B1): `GET /api/v2/overview`.
 *
 * The route is NOT audited and answers "bound" from the platform row, never through the audited
 * binding read, so polling it writes no audit row (contract §0.3). It is re-read every 30 s while
 * the tab is visible, and a 30 s `staleTime` keeps a quick return to the screen from firing a
 * second request (decisions SF3). There is no `Cache-Control` on the route: every client fetch
 * is `no-store` (`lib/api.ts`).
 *
 * The key carries the instance id as its second element. The shell does not clear the cache on
 * an instance switch, and a count from the previous instance under the new instance's header
 * would be the worst kind of wrong: plausible. `[OVERVIEW_PATH]` still reaches every instance's
 * entry by prefix, which is what P12's throttled invalidation uses.
 */
export const OVERVIEW_PATH = "/api/v2/overview";

/** Poll interval and stale time, both 30 s (decisions SF3). */
export const OVERVIEW_POLL_MS = 30_000;

export function overviewQueryKey(instanceId: string | null): [string, string | null] {
  return [OVERVIEW_PATH, instanceId];
}

export function useOverview(enabled = true): UseQueryResult<Overview> {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id ?? null;
  return useQuery<Overview>({
    queryKey: overviewQueryKey(instanceId),
    enabled: enabled && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<Overview>(OVERVIEW_PATH, { signal }),
    staleTime: OVERVIEW_POLL_MS,
    refetchInterval: OVERVIEW_POLL_MS,
    // Only while the tab is visible: a hidden tab has nobody to show the numbers to.
    refetchIntervalInBackground: false,
  });
}

/**
 * The transformation list and graph the tests card attributes with, read once: a 5-minute stale
 * time, and no poll (contract §3). The list keeps the list hook's rule: a 404 is "this deployment
 * has no transformations API" and resolves to null, never an error.
 *
 * Every list this screen reads is keyed `[path, instanceId]`, like the overview itself (review
 * round 2, SF1). The shell clears no cache on an instance switch and this screen does not remount,
 * so a key without the instance kept the previous instance's rows: the tests card named its
 * transformations and "Run now" offered its connections. The default query function still reads
 * the path from `[0]`, and the other screens' `[path]` invalidations reach these entries by
 * prefix. The cost: these entries are no longer shared with the Transformations, Connections and
 * Schedules screens' `[path]` entries, so a visit there and back reads once more.
 *
 * `enabled` is the caller's permission check: a read the role cannot make is never sent, because
 * a refused read writes an `auth.denied` row. No read is sent before an instance is selected.
 */
export const OVERVIEW_TRANSFORMATIONS_STALE_MS = 5 * 60_000;

/** The selected instance's id, the second element of every key on this screen. */
function useInstanceId(): string | null {
  return useTenantInstance().selectedInstance?.id ?? null;
}

export function useOverviewDbtJobs(enabled: boolean): UseQueryResult<DbtJobView[] | null> {
  const instanceId = useInstanceId();
  return useQuery<DbtJobView[] | null>({
    queryKey: [DBT_JOBS_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    staleTime: OVERVIEW_TRANSFORMATIONS_STALE_MS,
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest<DbtJobView[]>(DBT_JOBS_PATH, { signal });
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useOverviewTransformationGraph(
  enabled: boolean,
): UseQueryResult<TransformationGraph> {
  const instanceId = useInstanceId();
  return useQuery<TransformationGraph>({
    queryKey: [DBT_JOBS_GRAPH_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    staleTime: OVERVIEW_TRANSFORMATIONS_STALE_MS,
  });
}

/** #841: the newest transform steps read past one in flight; enough to find a finished one. */
export const OVERVIEW_RECENT_STEPS = 5;

/**
 * #841: while the overview's newest transform step is queued or running, the newest steps
 * (`GET /dbt-jobs/runs`, not audited), so the tests card can show the newest FINISHED one. Sent
 * only while that step is in flight; keyed by its id and status, so each new step in flight reads
 * the page again and a step that finished in between is in it.
 */
export function useOverviewRecentSteps(
  enabled: boolean,
  newest: DbtJobRunRow | null,
): UseQueryResult<DbtJobRunRow[]> {
  const instanceId = useInstanceId();
  const inFlight = newest !== null && transformStepPresentation(newest).isActive;
  return useQuery<DbtJobRunRow[]>({
    queryKey: [DBT_JOBS_RUNS_PATH, instanceId, "overview", newest?.runId, newest?.stepStatus],
    enabled: enabled && inFlight && instanceId !== null,
    staleTime: OVERVIEW_POLL_MS,
    // A step behind the newest can finish while the newest waits: re-read with the overview.
    refetchInterval: OVERVIEW_POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }) => (await listDbtJobRuns(OVERVIEW_RECENT_STEPS, signal)).rows,
  });
}

/**
 * The connection list, for the names of what the last five runs imported and (#1212) for the
 * Connections card's counts, which use the Connections list's own status words
 * (`connectionStatusOf`), so the card counts exactly what the list shows.
 * `GET /dataflows` is the unaudited list (never the audited `GET /dataflows/:id`). Re-read with
 * the overview, every 30 s while the tab is visible, and (#2094) by a run's own frames through
 * `overviewConnectionsFilter`: the Connections list's filter (`instanceListFilter`) never matched
 * this `[path, instanceId]` key, so the card read a finished run as Running for up to 30 s.
 */
export function overviewConnectionsKey(instanceId: string | null): [string, string | null] {
  return [DATAFLOWS_PATH, instanceId];
}

/**
 * #2094: the Overview's own copy of the connection list, in whichever instance, and nothing else
 * under `DATAFLOWS_PATH` (the Connections list's `[path, { instance }]` entry has its own filter).
 */
export function overviewConnectionsFilter(): {
  queryKey: [string];
  predicate: (query: { queryKey: readonly unknown[] }) => boolean;
} {
  return {
    queryKey: [DATAFLOWS_PATH],
    predicate: (query) =>
      query.queryKey.length === 2 &&
      (typeof query.queryKey[1] === "string" || query.queryKey[1] === null),
  };
}

/**
 * #2094 review: every connection list a run start or finish changes, the Connections list's
 * `[path, { instance }]` entry and the Overview's own `[path, instanceId]` copy, and nothing else
 * under `DATAFLOWS_PATH` (never the audited `GET /dataflows/:id`). The live feed and the start
 * paths (Run now, Import selected) share it, so neither copy waits for its own poll.
 */
export function invalidateConnectionLists(
  queryClient: QueryClient,
  refetchType: "active" | "none" = "active",
): void {
  void queryClient.invalidateQueries({ ...instanceListFilter(DATAFLOWS_PATH), refetchType });
  void queryClient.invalidateQueries({ ...overviewConnectionsFilter(), refetchType });
}

export function useOverviewConnections(enabled: boolean): UseQueryResult<DataflowSummary[]> {
  const instanceId = useInstanceId();
  return useQuery<DataflowSummary[]>({
    queryKey: overviewConnectionsKey(instanceId),
    enabled: enabled && instanceId !== null,
    staleTime: OVERVIEW_POLL_MS,
    refetchInterval: OVERVIEW_POLL_MS,
    refetchIntervalInBackground: false,
  });
}
